import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import AuthImage from "../components/AuthImage";
import { Lock, Users, ThumbsUp, ThumbsDown, Image as ImageIcon, UserPlus, X, Camera, FolderPlus, Trash2, Plus, Film } from "lucide-react";
import MediaPlayerModal from "../components/MediaPlayerModal";

export default function GroupView() {
  const { t } = useTranslation();
  const TABS = [
    { k: "innlegg",   label: t("group_view.tab_posts") },
    { k: "galleri",   label: t("group_view.tab_gallery") },
    { k: "album",     label: "Album" },
    { k: "medlemmer", label: t("group_view.tab_members") },
    { k: "om",        label: t("group_view.tab_about") },
  ];
  const { groupId } = useParams();
  const { user } = useAuth();
  const [data, setData]   = useState(null);
  const [tab, setTab]     = useState("innlegg");
  const [posts, setPosts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [activeMedia, setActiveMedia] = useState(null);
  const [body, setBody]   = useState("");
  const [postMedia, setPostMedia] = useState(null); // { url, kind, id }
  const [postMediaBusy, setPostMediaBusy] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy]   = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const postMediaInputRef = useRef(null);

  const load = () => api.get(`/groups/${groupId}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupId]);

  useEffect(() => {
    if (tab === "innlegg") api.get(`/groups/${groupId}/posts`).then((r)=>setPosts(r.data)).catch(()=>{});
    if (tab === "galleri") api.get(`/groups/${groupId}/gallery`).then((r)=>setGallery(r.data)).catch(()=>{});
  }, [tab, groupId]);

  const join = async () => {
    setError(""); setBusy(true);
    try { await api.post(`/groups/${groupId}/join`); await load(); }
    catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const leave = async () => {
    setError(""); setBusy(true);
    try { await api.post(`/groups/${groupId}/leave`); await load(); }
    catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const post = async (e) => {
    e.preventDefault();
    setError("");
    if (!body.trim() && !postMedia) return;
    try {
      const payload = { body: body.trim() || " " };
      if (postMedia) {
        payload.media_url = postMedia.url;
        payload.media_kind = postMedia.kind;
      }
      await api.post(`/groups/${groupId}/posts`, payload);
      setBody("");
      setPostMedia(null);
      const r = await api.get(`/groups/${groupId}/posts`);
      setPosts(r.data);
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const uploadPostMedia = async (file) => {
    if (!file) return;
    setPostMediaBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/groups/${groupId}/posts/upload-media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPostMedia({ url: r.data.url, kind: r.data.kind, id: r.data.id });
    } catch (err) { setError(formatApiError(err)); }
    finally { setPostMediaBusy(false); }
  };

  const uploadGroupAvatar = async (file) => {
    if (!file) return;
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/groups/${groupId}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const uploadGroupCover = async (file) => {
    if (!file) return;
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/groups/${groupId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const react = async (postId, type) => {
    try {
      await api.post(`/groups/${groupId}/posts/${postId}/react`, { type });
      const r = await api.get(`/groups/${groupId}/posts`);
      setPosts(r.data);
    } catch { /* ignore */ }
  };

  if (!data) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("group_view.loading")}</div></Layout>;
  const { group, members, is_member, has_pending_request } = data;
  const isOwner = user && group && group.owner_id === user.id;
  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const canManage = isOwner || isAdmin;

  return (
    <Layout withSidebar={false}>
      <div className="font-meta mb-4">
        <Link to="/grupper" className="hover:text-[var(--cream)]">{t("group_view.breadcrumb")}</Link>
      </div>

      {/* Cover */}
      <div className="relative w-full h-40 sm:h-56 mb-6 bg-gradient-to-br from-[var(--magenta)]/15 to-[var(--copper)]/10 border border-white/5 overflow-hidden" data-testid="group-cover">
        {group.cover_url && (
          <img src={group.cover_url} alt="" className="w-full h-full object-cover" />
        )}
        {canManage && (
          <>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="group-cover-file"
              onChange={(e) => { uploadGroupCover(e.target.files?.[0]); e.target.value = ""; }}
            />
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/70 hover:bg-black text-[var(--cream)] font-meta text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-white/15"
              data-testid="group-cover-upload-btn"
            >
              <Camera className="h-3 w-3" /> {group.cover_url ? "Endre cover" : "Last opp cover"}
            </button>
          </>
        )}
      </div>

      {/* Group header */}
      <div className="border-b border-white/10 pb-8 mb-8">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="relative shrink-0">
            <div className="h-24 w-24 rounded-sm bg-gradient-to-br from-[var(--magenta)]/30 to-[var(--copper)]/20 flex items-center justify-center overflow-hidden">
              {group.avatar_url ? (
                <img src={group.avatar_url} alt="" className="w-full h-full object-cover" data-testid="group-avatar-img" />
              ) : (
                <Users className="h-10 w-10 text-[var(--magenta)]" />
              )}
            </div>
            {canManage && (
              <>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="group-avatar-file"
                  onChange={(e) => { uploadGroupAvatar(e.target.files?.[0]); e.target.value = ""; }}
                />
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute -bottom-2 -right-2 h-7 w-7 rounded-full bg-[var(--magenta)] text-black flex items-center justify-center hover:opacity-90"
                  data-testid="group-avatar-upload-btn"
                  title="Endre gruppebilde"
                >
                  <Camera className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-meta mb-2 flex items-center gap-3">
              <span className="text-[var(--magenta)]">{group.category || t("group_view.topic")}</span>
              {group.is_private && <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> {t("group_view.closed")}</span>}
            </div>
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)] leading-tight mb-3">{group.name}</h1>
            <p className="font-fraunces text-lg text-[var(--cream)]/80 leading-relaxed max-w-2xl">{group.description}</p>
            <div className="font-meta mt-4 flex flex-wrap gap-x-6 gap-y-1">
              <span>{t("group_view.members_count", { count: group.member_count })}</span>
              <span>{t("group_view.posts_count", { count: group.post_count || 0 })}</span>
              {group.last_post_at && <span>{t("group_view.active_ago", { ago: formatRelative(group.last_post_at) })}</span>}
            </div>
          </div>
          {user && user !== false && (
            <div className="flex items-center gap-2 shrink-0">
              {(isOwner || isAdmin) && (
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  data-testid="invite-members-btn"
                  className="px-4 py-2 border border-white/15 hover:border-[var(--magenta)] text-[var(--cream)] font-meta text-xs tracking-[0.15em] inline-flex items-center gap-2"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Inviter
                </button>
              )}
              {is_member ? (
                <button onClick={leave} disabled={busy} className="btn-ghost" data-testid="leave-group-button">
                  {t("group_view.leave_group")}
                </button>
              ) : has_pending_request ? (
                <div className="px-4 py-2 border border-[var(--magenta)]/40 text-[var(--magenta)] font-meta text-xs tracking-[0.15em]" data-testid="pending-request-badge">
                  {t("group_view.pending_approval")}
                </div>
              ) : (
                <button onClick={join} disabled={busy} className="btn-magenta" data-testid="join-group-button">
                  {group.requires_approval ? t("group_view.request_membership") : t("group_view.join_group")}
                </button>
              )}
            </div>
          )}
        </div>
        {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 mt-4">{error}</div>}
      </div>

      {/* Pending join-requests panel (only for owner/admin) */}
      {(isOwner || isAdmin) && group.requires_approval && (group.pending_count || 0) > 0 && (
        <PendingRequestsPanel groupId={groupId} onChange={load} />
      )}

      {/* Tabs */}
      <div className="flex gap-6 border-b border-white/10 mb-10">
        {TABS.map((t) => (
          <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`group-tab-${t.k}`}
            className={`pb-3 font-manrope text-[11px] uppercase tracking-[0.25em] transition-colors relative ${
              tab === t.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}>
            {t.label}
            {tab === t.k && <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--magenta)]" />}
          </button>
        ))}
      </div>

      {/* GUEST-GATE: uinnloggede kan se gruppens navn/beskrivelse men ikke innhold */}
      {(!user || user === false) ? (
        <div className="editorial-card p-10 text-center max-w-2xl mx-auto" data-testid="group-guest-gate">
          <div className="h-14 w-14 rounded-full bg-[var(--magenta)]/15 mx-auto mb-5 flex items-center justify-center">
            <Lock className="h-6 w-6 text-[var(--magenta)]" />
          </div>
          <h3 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Logg inn for å se innhold</h3>
          <p className="font-manrope text-sm text-[var(--cream-soft)] max-w-sm mx-auto mb-6 leading-relaxed">
            Innhold i grupper er kun for innloggede medlemmer. Registrer deg gratis eller logg inn for å se innlegg, galleri og medlemmer.
          </p>
          <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="group-guest-login-cta">
            Logg inn
          </Link>
        </div>
      ) : (
      <>
      {/* INNLEGG */}
      {tab === "innlegg" && (
        <div className="max-w-3xl">
          {is_member && (
            <form onSubmit={post} className="mb-10 editorial-card p-5" data-testid="group-post-form">
              <div className="font-meta mb-3">{t("group_view.new_post")}</div>
              <textarea value={body} onChange={(e)=>setBody(e.target.value)} rows={3}
                        placeholder={t("group_view.post_placeholder")}
                        className="input-editorial mb-3" data-testid="group-post-body" />
              {postMedia && (
                <div className="relative mb-3 inline-block" data-testid="group-post-media-preview">
                  {postMedia.kind === "video" ? (
                    <video src={postMedia.url} className="max-h-48 border border-white/10" controls />
                  ) : (
                    <img src={postMedia.url} className="max-h-48 border border-white/10" alt="" />
                  )}
                  <button
                    type="button"
                    onClick={() => setPostMedia(null)}
                    className="absolute top-1 right-1 h-6 w-6 bg-black/80 text-[var(--cream)] flex items-center justify-center hover:bg-black"
                    data-testid="group-post-media-remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <input
                ref={postMediaInputRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                data-testid="group-post-media-file"
                onChange={(e) => { uploadPostMedia(e.target.files?.[0]); e.target.value = ""; }}
              />
              <div className="flex items-center gap-3">
                <button className="btn-magenta" data-testid="group-post-submit" disabled={postMediaBusy}>
                  {t("group_view.publish")}
                </button>
                <button
                  type="button"
                  onClick={() => postMediaInputRef.current?.click()}
                  disabled={postMediaBusy}
                  className="px-3 py-2 border border-white/15 hover:border-[var(--magenta)] text-[var(--cream-soft)] hover:text-[var(--cream)] font-meta text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="group-post-attach-btn"
                >
                  <ImageIcon className="h-3.5 w-3.5" /> {postMediaBusy ? "Laster…" : "Bilde / video"}
                </button>
              </div>
            </form>
          )}
          {posts.length === 0 ? (
            <div className="font-fraunces italic text-[var(--cream-soft)] py-12 text-center">
              {t("group_view.no_posts")}
            </div>
          ) : (
            <ul className="space-y-8">
              {posts.map((p) => (
                <li key={p.id} className="border-b border-white/5 pb-8" data-testid={`group-post-${p.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="h-9 w-9 rounded-sm border border-white/10">
                      <AvatarFallback className="bg-[var(--magenta)] text-black text-xs font-bold rounded-sm">
                        {(p.author?.username || "?").slice(0,2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <Link to={`/profile/${p.author?.username}`} className="font-fraunces italic text-lg text-[var(--cream)] hover:text-[var(--magenta)]">
                        {p.author?.username}
                      </Link>
                      <div className="font-meta">
                        {p.author?.gender && <span className="capitalize">{p.author.gender}</span>}
                        {p.author?.age && <span>, {p.author.age}</span>}
                        {p.author?.city && <span> · {p.author.city}</span>}
                        <span> · {formatRelative(p.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <p className="font-fraunces text-[var(--cream)] leading-[1.7] whitespace-pre-wrap mb-3 ml-12">
                    {p.body}
                  </p>
                  {p.image_id && (
                    <div className="ml-12 mb-3 max-w-md">
                      <AuthImage imageId={p.image_id} className="w-full" />
                    </div>
                  )}
                  {p.media_locked === "vip_video" && (
                    <Link to="/vip" className="ml-12 mb-3 max-w-md aspect-video bg-black/80 border border-white/10 flex flex-col items-center justify-center gap-2" data-testid={`group-post-locked-${p.id}`}>
                      <Lock className="h-6 w-6 text-[var(--magenta)]" />
                      <span className="font-meta text-xs uppercase tracking-[0.15em] text-[var(--cream)]">Film krever VIP</span>
                    </Link>
                  )}
                  {p.media_url && (
                    <div className="ml-12 mb-3 max-w-md" data-testid={`group-post-media-${p.id}`}>
                      {p.media_kind === "video" ? (
                        <video src={p.media_url} className="w-full border border-white/10" controls />
                      ) : (
                        <img src={p.media_url} className="w-full border border-white/10" alt="" />
                      )}
                    </div>
                  )}
                  <div className="ml-12 flex items-center gap-4">
                    <button onClick={()=>react(p.id, "like")} className="font-meta inline-flex items-center gap-1.5 hover:text-[var(--magenta)]" data-testid={`like-${p.id}`}>
                      <ThumbsUp className="h-3.5 w-3.5" /> {p.like_count || 0}
                    </button>
                    <button onClick={()=>react(p.id, "dislike")} className="font-meta inline-flex items-center gap-1.5 hover:text-[var(--cream)]" data-testid={`dislike-${p.id}`}>
                      <ThumbsDown className="h-3.5 w-3.5" /> {p.dislike_count || 0}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* GALLERI */}
      {tab === "galleri" && (
        gallery.length === 0 ? (
          <div className="text-center py-16" data-testid="group-gallery-empty">
            <ImageIcon className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
            <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">{t("group_view.no_images")}</div>
            <p className="font-manrope text-sm text-[var(--cream-faint)] mt-2">{t("group_view.gallery_hint")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5"
               data-testid="group-gallery-grid">
            {gallery.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveMedia(item)}
                className="group relative aspect-[3/4] overflow-hidden bg-black ring-1 ring-white/5 hover:ring-[var(--magenta)]/60 transition-all"
                data-testid={`group-gallery-${item.id}`}
              >
                <AuthImage
                  imageId={item.id}
                  alt={item.caption || ""}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent pointer-events-none" />
                <div className="absolute bottom-2 left-2 right-2 text-left">
                  <div className="font-fraunces italic text-sm text-[var(--cream)] truncate">
                    {item.owner?.username || t("group_view.unknown")}
                  </div>
                  {item.posted_at && (
                    <div className="font-meta text-[9px] text-[var(--cream-soft)] mt-0.5">
                      {formatRelative(item.posted_at)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {/* ALBUM */}
      {tab === "album" && (
        <GroupAlbums
          groupId={groupId}
          canManage={canManage}
          isMember={is_member}
        />
      )}

      {/* MEDLEMMER */}
      {tab === "medlemmer" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map((m) => {
            const isMemberOwner = m.role === "owner";
            const isMemberAdmin = m.role === "admin";
            const roleLabel = isMemberOwner
              ? t("group_view.role_owner")
              : isMemberAdmin
                ? "Admin"
                : t("group_view.role_member");
            const showRoleControls = canManage && !isMemberOwner && user && m.user_id !== user.id;
            const toggleRole = async (e) => {
              e.preventDefault(); e.stopPropagation();
              const newRole = isMemberAdmin ? "member" : "admin";
              try {
                await api.patch(`/groups/${groupId}/members/${m.user_id}/role`, { role: newRole });
                await load();
              } catch (err) { alert(formatApiError(err)); }
            };
            return (
              <div key={m.id} className="editorial-card p-4 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
                <Link to={`/profile/${m.user.username}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <Avatar className="h-10 w-10 rounded-sm border border-white/10">
                    <AvatarFallback className="bg-[var(--magenta)] text-black text-sm font-bold rounded-sm">
                      {(m.user.username || "?").slice(0,2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="font-fraunces italic text-[var(--cream)] truncate">{m.user.username}</div>
                    <div className={`font-meta text-[10px] tracking-[0.2em] uppercase ${isMemberAdmin || isMemberOwner ? "text-[var(--magenta)]" : "text-[var(--cream-soft)]"}`}>
                      {roleLabel}
                    </div>
                  </div>
                </Link>
                {showRoleControls && (
                  <button
                    type="button"
                    onClick={toggleRole}
                    data-testid={`member-role-toggle-${m.user_id}`}
                    className="px-2 py-1.5 border border-white/15 hover:border-[var(--magenta)] hover:text-[var(--magenta)] text-[var(--cream-soft)] font-meta text-[10px] tracking-[0.2em] uppercase whitespace-nowrap"
                  >
                    {isMemberAdmin ? "Fjern admin" : "Gjør admin"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* OM */}
      {tab === "om" && (
        <div className="max-w-2xl space-y-6">
          <div>
            <div className="font-meta mb-2">{t("group_view.description")}</div>
            <p className="font-fraunces text-lg text-[var(--cream)]/90 leading-relaxed whitespace-pre-wrap">{group.description}</p>
          </div>
          {group.rules && (
            <div>
              <div className="font-meta mb-2">{t("group_view.rules")}</div>
              <p className="font-fraunces text-base text-[var(--cream)]/80 leading-relaxed whitespace-pre-wrap">{group.rules}</p>
            </div>
          )}
          {group.tags?.length > 0 && (
            <div>
              <div className="font-meta mb-2">{t("group_view.tags")}</div>
              <div className="flex flex-wrap gap-2">
                {group.tags.map((tg) => (
                  <span key={tg} className="font-manrope text-xs text-[var(--cream-soft)] border border-white/10 px-3 py-1">#{tg}</span>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="font-meta mb-2">{t("group_view.details")}</div>
            <dl className="space-y-2.5 font-manrope text-sm">
              {[
                [t("group_view.category"),     group.category],
                [t("group_view.visibility"),   group.is_private ? t("group_view.closed") : t("group_view.public")],
                [t("group_view.created"),      formatRelative(group.created_at)],
                [t("group_view.min_account_age"), group.min_account_age_days ? t("group_view.days", { n: group.min_account_age_days }) : t("group_view.none")],
                [t("group_view.requires_vip"), group.require_vip ? t("group_view.yes") : t("group_view.no")],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 pb-2">
                  <dt className="text-[var(--cream-soft)]">{k}</dt>
                  <dd className="text-[var(--cream)] capitalize">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      </>
      )}

      <MediaPlayerModal media={activeMedia} items={gallery} onClose={() => setActiveMedia(null)} />

      {inviteOpen && (
        <InviteToGroupModal
          groupId={groupId}
          groupName={group.name}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </Layout>
  );
}

function InviteToGroupModal({ groupId, groupName, onClose }) {
  const [input, setInput] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [query, setQuery] = React.useState("");

  // Auto-forslag mens man skriver siste brukernavn (etter komma/space)
  React.useEffect(() => {
    const parts = input.split(/[,\s]+/).filter(Boolean);
    const last = parts[parts.length - 1] || "";
    if (last.length < 2) { setSuggestions([]); setQuery(""); return; }
    setQuery(last);
    const ctl = new AbortController();
    api.get(`/search/members?q=${encodeURIComponent(last)}&per_page=6`, { signal: ctl.signal })
      .then((r) => setSuggestions(r.data?.results || []))
      .catch(() => {});
    return () => ctl.abort();
  }, [input]);

  const addSuggestion = (username) => {
    const parts = input.split(/[,\s]+/);
    parts[parts.length - 1] = username;
    setInput(parts.join(", ") + ", ");
    setSuggestions([]);
  };

  const submit = async () => {
    setError(""); setResult(null);
    const usernames = input.split(/[,\s]+/).map((s) => s.trim().replace(/^@/, "")).filter(Boolean);
    if (usernames.length === 0) { setError("Skriv minst ett brukernavn"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/groups/${groupId}/invite`, { usernames });
      setResult(r.data);
      setInput("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 pt-24 sm:pt-4 items-start sm:items-center"
      onClick={() => !submitting && onClose()}
      data-testid="invite-modal"
    >
      <div
        className="w-full max-w-md bg-[var(--noir-elev)] border border-white/10 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--cream-soft)] hover:text-[var(--cream)]"
          data-testid="invite-modal-close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Inviter til «{groupName}»</h2>
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-5">
          Skriv ett eller flere brukernavn (separert med komma eller mellomrom).
        </p>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="@brukernavn1, @brukernavn2"
          rows={3}
          className="input-editorial w-full p-3 text-sm"
          data-testid="invite-usernames-input"
        />
        {suggestions.length > 0 && (
          <div className="mt-2 border border-white/10 bg-black/40">
            {suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => addSuggestion(s.username)}
                className="w-full text-left px-3 py-2 hover:bg-white/[0.04] flex items-center gap-2"
                data-testid={`invite-suggestion-${s.username}`}
              >
                {s.avatar_url ? (
                  <img src={s.avatar_url} alt={s.username} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-[var(--noir)] flex items-center justify-center text-[10px] text-[var(--cream)]">
                    {s.username.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <span className="font-manrope text-sm text-[var(--cream)]">{s.username}</span>
              </button>
            ))}
          </div>
        )}
        {error && <div className="text-sm text-red-400 font-manrope mt-3">{error}</div>}
        {result && (
          <div className="mt-4 space-y-1 font-manrope text-xs" data-testid="invite-result">
            {result.invited?.length > 0 && (
              <div className="text-green-400">Invitert: {result.invited.join(", ")}</div>
            )}
            {result.skipped?.length > 0 && (
              <div className="text-[var(--cream-soft)]">
                Hoppet over: {result.skipped.map((s) => `${s.username} (${s.reason})`).join(", ")}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2.5 border border-white/15 text-[var(--cream-soft)] font-meta tracking-[0.15em] text-xs hover:text-[var(--cream)] disabled:opacity-50"
          >
            Lukk
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !input.trim()}
            data-testid="invite-submit-btn"
            className="flex-1 py-2.5 bg-[var(--magenta)] text-white font-meta tracking-[0.15em] text-xs hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? "Sender…" : "Send invitasjon"}
          </button>
        </div>
      </div>
    </div>
  );
}



function PendingRequestsPanel({ groupId, onChange }) {
  const { t } = useTranslation();
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get(`/groups/${groupId}/join-requests`)
      .then((r) => setRequests(r.data?.requests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [groupId]);

  React.useEffect(() => { load(); }, [load]);

  const decide = async (request_id, approve) => {
    setBusy(request_id);
    try {
      await api.post(`/groups/${groupId}/join-requests/decide`, { request_id, approve });
      await load();
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || t("group_view.cannot_handle_request"));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <div className="panel p-5 mb-8" data-testid="pending-requests-panel">
      <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-3">
        {t("group_view.pending_title", { count: requests.length })}
      </div>
      <div className="space-y-2">
        {requests.map((r) => (
          <div key={r.id} className="flex items-center justify-between border border-white/5 px-4 py-3" data-testid={`pending-row-${r.user?.username}`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {r.user?.avatar_url ? (
                <img src={r.user.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <div className="h-9 w-9 rounded-full bg-[var(--magenta)]/20 flex items-center justify-center text-[var(--magenta)] font-meta text-xs">
                  {r.user?.username?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div>
                <div className="text-sm text-[var(--cream)]">@{r.user?.username}</div>
                <div className="text-[11px] text-[var(--cream-faint)]">
                  {r.user?.age ? `${t("group_view.age_years", { n: r.user.age })} · ` : ""}{r.user?.gender || ""}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => decide(r.id, true)}
                disabled={busy === r.id}
                className="px-3 py-1.5 text-[10px] font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40"
                data-testid={`approve-${r.user?.username}`}
              >
                {t("group_view.approve")}
              </button>
              <button
                onClick={() => decide(r.id, false)}
                disabled={busy === r.id}
                className="px-3 py-1.5 text-[10px] font-meta tracking-[0.15em] border border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)] disabled:opacity-40"
                data-testid={`reject-${r.user?.username}`}
              >
                {t("group_view.decline")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function GroupAlbums({ groupId, canManage, isMember }) {
  const [albums, setAlbums] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [activeAlbum, setActiveAlbum] = React.useState(null);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(`/groups/${groupId}/albums`);
      setAlbums(r.data?.items || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [groupId]);

  React.useEffect(() => { load(); }, [load]);

  const deleteAlbum = async (album_id) => {
    if (!window.confirm("Slette albumet og alt innhold?")) return;
    try {
      await api.delete(`/groups/${groupId}/albums/${album_id}`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (!isMember) {
    return (
      <div className="font-fraunces italic text-[var(--cream-soft)] py-12 text-center" data-testid="albums-not-member">
        Du må være medlem for å se gruppens album.
      </div>
    );
  }

  if (activeAlbum) {
    return (
      <GroupAlbumDetail
        groupId={groupId}
        album={activeAlbum}
        isMember={isMember}
        canManage={canManage}
        onBack={() => { setActiveAlbum(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="group-albums-section">
      <div className="flex items-center justify-between mb-6">
        <div className="font-meta text-[var(--cream-soft)]">
          {loading ? "Laster…" : `${albums.length} album`}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="album-create-btn"
            className="px-3 py-2 bg-[var(--magenta)] text-black font-meta text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 hover:opacity-90"
          >
            <FolderPlus className="h-3.5 w-3.5" /> Nytt album
          </button>
        )}
      </div>
      {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 mb-4">{error}</div>}
      {albums.length === 0 ? (
        <div className="font-fraunces italic text-[var(--cream-soft)] py-12 text-center" data-testid="albums-empty">
          Ingen album ennå. {canManage ? "Opprett det første!" : ""}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {albums.map((a) => (
            <div key={a.id} className="border border-white/10 hover:border-[var(--magenta)]/60 transition-colors group" data-testid={`album-card-${a.id}`}>
              <button
                type="button"
                onClick={() => setActiveAlbum(a)}
                className="w-full aspect-[4/3] bg-black overflow-hidden block"
              >
                {a.cover_url ? (
                  <img src={a.cover_url} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-10 w-10 text-[var(--cream-faint)]" />
                  </div>
                )}
              </button>
              <div className="p-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setActiveAlbum(a)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="font-fraunces italic text-[var(--cream)] truncate">{a.name}</div>
                  <div className="font-meta text-[10px] text-[var(--cream-soft)]">
                    {a.media_count || 0} medier{a.is_default ? " · standard" : ""}
                  </div>
                </button>
                {canManage && !a.is_default && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteAlbum(a.id); }}
                    className="text-[var(--cream-faint)] hover:text-[var(--magenta)]"
                    data-testid={`album-delete-${a.id}`}
                    title="Slett album"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {createOpen && (
        <CreateAlbumModal
          groupId={groupId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateAlbumModal({ groupId, onClose, onCreated }) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post(`/groups/${groupId}/albums`, { name: name.trim(), description: description.trim() });
      onCreated();
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 pt-24 sm:pt-4" onClick={() => !busy && onClose()} data-testid="create-album-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[var(--noir-elev)] border border-white/10 p-6 relative"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[var(--cream-soft)] hover:text-[var(--cream)]" data-testid="create-album-close">
          <X className="h-5 w-5" />
        </button>
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">Nytt album</h2>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Albumnavn"
          maxLength={80}
          required
          className="input-editorial w-full p-3 mb-3"
          data-testid="create-album-name"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Beskrivelse (valgfritt)"
          rows={3}
          maxLength={500}
          className="input-editorial w-full p-3 mb-3"
          data-testid="create-album-desc"
        />
        {error && <div className="text-sm text-red-400 font-manrope mb-3">{error}</div>}
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onClose} disabled={busy} className="flex-1 py-2.5 border border-white/15 text-[var(--cream-soft)] font-meta tracking-[0.15em] text-xs hover:text-[var(--cream)] disabled:opacity-50">
            Avbryt
          </button>
          <button type="submit" disabled={busy || !name.trim()} data-testid="create-album-submit" className="flex-1 py-2.5 bg-[var(--magenta)] text-white font-meta tracking-[0.15em] text-xs hover:opacity-90 disabled:opacity-40">
            {busy ? "Oppretter…" : "Opprett"}
          </button>
        </div>
      </form>
    </div>
  );
}

function GroupAlbumDetail({ groupId, album, isMember, canManage, onBack }) {
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [active, setActive] = React.useState(null);
  const fileRef = React.useRef(null);
  const PAGE = 60;

  const fetchPage = React.useCallback(async (skip) => {
    const r = await api.get(`/groups/${groupId}/albums/${album.id}/media`, {
      params: { skip, limit: PAGE },
    });
    return r.data || { items: [], total: 0, has_more: false };
  }, [groupId, album.id]);

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await fetchPage(0);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setHasMore(!!r.has_more);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [fetchPage]);

  React.useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const r = await fetchPage(items.length);
      setItems((prev) => [...prev, ...(r.items || [])]);
      setHasMore(!!r.has_more);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoadingMore(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/groups/${groupId}/albums/${album.id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const deleteMedia = async (mid) => {
    if (!window.confirm("Slette dette mediet?")) return;
    try {
      await api.delete(`/groups/${groupId}/albums/${album.id}/media/${mid}`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div data-testid="album-detail">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={onBack} className="font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] mb-2" data-testid="album-detail-back">← Tilbake til album</button>
          <h2 className="font-fraunces italic text-3xl text-[var(--cream)]">{album.name}</h2>
          {album.description && <p className="font-manrope text-sm text-[var(--cream-soft)] mt-1">{album.description}</p>}
          {total > 0 && (
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1" data-testid="album-total-count">
              {items.length} av {total} medier
            </div>
          )}
        </div>
        {isMember && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              data-testid="album-upload-file"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="album-upload-btn"
              className="px-3 py-2 bg-[var(--magenta)] text-black font-meta text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" /> {uploading ? "Laster…" : "Last opp"}
            </button>
          </div>
        )}
      </div>
      {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 mb-4">{error}</div>}
      {loading ? (
        <div className="font-meta text-[var(--cream-soft)] py-12 text-center">Laster…</div>
      ) : items.length === 0 ? (
        <div className="font-fraunces italic text-[var(--cream-soft)] py-12 text-center" data-testid="album-empty">
          Ingen medier i dette albumet ennå.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {items.map((m) => (
              <div key={m.id} className="relative group aspect-[3/4] bg-black ring-1 ring-white/5 overflow-hidden" data-testid={`album-media-${m.id}`}>
                <button onClick={() => !m.locked && setActive(m)} className="w-full h-full block">
                  {m.locked === "vip_video" ? (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2" data-testid={`album-media-locked-${m.id}`}>
                      <Lock className="h-6 w-6 text-[var(--magenta)]" />
                      <span className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream)]">Film krever VIP</span>
                    </div>
                  ) : m.kind === "video" ? (
                    <div className="relative w-full h-full">
                      <video src={m.url} className="w-full h-full object-cover" preload="metadata" muted />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Film className="h-8 w-8 text-white/80" />
                      </div>
                    </div>
                  ) : (
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
                {(canManage) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteMedia(m.id); }}
                    className="absolute top-1 right-1 h-7 w-7 bg-black/80 text-[var(--cream)] hover:bg-[var(--magenta)] flex items-center justify-center opacity-0 group-hover:opacity-100"
                    data-testid={`album-media-delete-${m.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="album-load-more"
                className="px-5 py-2.5 border border-white/15 hover:border-[var(--magenta)] text-[var(--cream)] font-meta text-[10px] tracking-[0.2em] uppercase disabled:opacity-40"
              >
                {loadingMore ? "Laster…" : "Last flere"}
              </button>
            </div>
          )}
        </>
      )}
      {active && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="album-media-modal">
          <button onClick={() => setActive(null)} className="absolute top-4 right-4 text-[var(--cream)] hover:text-[var(--magenta)]"><X className="h-6 w-6" /></button>
          {active.kind === "video" ? (
            <video src={active.url} className="max-w-full max-h-full" controls autoPlay />
          ) : (
            <img src={active.url} alt="" className="max-w-full max-h-full object-contain" />
          )}
        </div>
      )}
    </div>
  );
}
